import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { GET_IMAGE, GET_IMAGE_CVES, GET_IMAGE_HEALTH_SCORE, GET_SIGNING_STATUS, GET_TEST_RESULTS, GET_DEPLOYED_INSTANCES } from '../graphql/queries';
import { DELETE_IMAGE } from '../graphql/mutations';
import OsIcon, { AmiImageIcon } from '../components/OsIcon';
import { formatArch } from '../utils/os-icons';

function severityColor(severity) {
  const map = { CRITICAL: '#d63031', HIGH: '#e17055', MEDIUM: '#fdcb6e', LOW: '#00b894' };
  return map[severity] || '#636e72';
}

function ComplianceBadge({ compliant, critical, high }) {
  if (!compliant || critical > 0) {
    return <span className="badge badge-error">NOT COMPLIANT</span>;
  }
  if (high > 0) {
    return <span className="badge badge-warning">REVIEW NEEDED</span>;
  }
  return <span className="badge badge-success">COMPLIANT</span>;
}

function CveTable({ platformId }) {
  const { data, loading } = useQuery(GET_IMAGE_CVES, {
    variables: { imagePlatformId: platformId },
    skip: !platformId,
  });

  if (!platformId) return null;
  if (loading) return <div className="loading">Loading CVEs...</div>;

  const cves = data?.listImageCves?.items || [];
  if (cves.length === 0) return <p style={{ padding: 12, color: '#636e72' }}>No CVE findings for this platform.</p>;

  const sorted = [...cves].sort((a, b) => {
    const order = { CRITICAL: 0, HIGH: 1, MEDIUM: 2, LOW: 3, INFORMATIONAL: 4 };
    return (order[a.severity] ?? 5) - (order[b.severity] ?? 5);
  });

  return (
    <table>
      <thead>
        <tr>
          <th>CVE ID</th>
          <th>Severity</th>
          <th>Score</th>
          <th>Package</th>
          <th>Fix Available</th>
          <th>Description</th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((cve) => (
          <tr key={cve.id}>
            <td>
              {cve.sourceUrl ? (
                <a href={cve.sourceUrl} target="_blank" rel="noopener noreferrer" className="link">{cve.cveId}</a>
              ) : (
                cve.cveId
              )}
            </td>
            <td>
              <span className="badge" style={{ background: severityColor(cve.severity) + '22', color: severityColor(cve.severity) }}>
                {cve.severity}
              </span>
            </td>
            <td>{cve.score ?? '-'}</td>
            <td>{cve.packageName ? `${cve.packageName} ${cve.packageVersion || ''}` : '-'}</td>
            <td>{cve.fixAvailable ? 'Yes' : 'No'}</td>
            <td style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {cve.title || cve.description || '-'}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function HealthBadge({ imageId }) {
  const { data } = useQuery(GET_IMAGE_HEALTH_SCORE, { variables: { imageId } });
  const score = data?.getImageHealthScore;
  if (!score) return null;
  const gradeColor = { A: '#00b894', B: '#00cec9', C: '#fdcb6e', D: '#e17055', F: '#d63031' };
  return (
    <Link to={`/scorecard/${imageId}`} style={{ textDecoration: 'none' }}>
      <span className="badge" style={{ background: (gradeColor[score.grade] || '#636e72') + '22', color: gradeColor[score.grade] || '#636e72', fontSize: 11 }}>
        Health: {score.grade} ({score.overallScore})
      </span>
    </Link>
  );
}

function SigningBadge({ imageId }) {
  const { data } = useQuery(GET_SIGNING_STATUS, { variables: { imageId } });
  const status = data?.getSigningStatus;
  if (!status) return null;
  return status.signed
    ? <span className="badge badge-success" style={{ fontSize: 11 }}>Signed</span>
    : <span className="badge badge-pending" style={{ fontSize: 11 }}>Unsigned</span>;
}

function TestBadge({ imageId }) {
  const { data } = useQuery(GET_TEST_RESULTS, { variables: { imageId } });
  const results = data?.getTestResults;
  if (!results) return null;
  return (
    <Link to={`/tests/${imageId}`} style={{ textDecoration: 'none' }}>
      <span className={`badge badge-${results.status === 'passed' ? 'success' : results.status === 'failed' ? 'error' : 'pending'}`} style={{ fontSize: 11 }}>
        Tests: {results.passed}/{results.totalTests}
      </span>
    </Link>
  );
}

function DeployedBadge({ imageId }) {
  const { data } = useQuery(GET_DEPLOYED_INSTANCES, { variables: { imageId } });
  const instances = data?.getDeployedInstances;
  if (!instances || instances.length === 0) return null;
  const regions = new Set(instances.map(i => i.region));
  return (
    <Link to={`/fleet/instances?amiIds=${imageId}`} style={{ textDecoration: 'none' }}>
      <span className="badge" style={{ background: '#0984e322', color: '#0984e3', fontSize: 11, padding: '4px 10px' }}>
        {instances.length} instance{instances.length !== 1 ? 's' : ''} in {regions.size} region{regions.size !== 1 ? 's' : ''}
      </span>
    </Link>
  );
}

export default function ImageDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, loading, error } = useQuery(GET_IMAGE, { variables: { id } });
  const [deleteImage, { loading: deleting }] = useMutation(DELETE_IMAGE);
  const [selectedPlatformId, setSelectedPlatformId] = useState(null);

  if (loading) return <div className="loading">Loading image details...</div>;
  if (error) return <div className="error">Failed to load image: {error.message}</div>;

  const image = data?.getImage;
  if (!image) return <div className="error">Image not found</div>;

  const handleDelete = async () => {
    if (!window.confirm(`Delete image ${image.name}? This will deregister all associated AMIs.`)) return;
    const region = image.platforms?.[0]?.region || 'us-east-1';
    try {
      await deleteImage({ variables: { id, region } });
      navigate('/images');
    } catch (err) {
      // Error is displayed by Apollo error handling
    }
  };

  return (
    <div>
      <h2 className="page-title">
        <AmiImageIcon size={24} /> {image.name}
        <span style={{ marginLeft: 12, display: 'inline-flex', gap: 6, alignItems: 'center', verticalAlign: 'middle' }}>
          <HealthBadge imageId={id} />
          <SigningBadge imageId={id} />
          <TestBadge imageId={id} />
          <DeployedBadge imageId={id} />
        </span>
      </h2>

      <div className="card">
        <h3>Image Details</h3>
        <div className="detail-grid">
          <div className="detail-item"><label>Name</label><p>{image.name}</p></div>
          <div className="detail-item"><label>Blueprint</label><p><OsIcon name={image.blueprintName} showLabel /></p></div>
          <div className="detail-item"><label>Version</label><p>{image.blueprintVersion}</p></div>
          <div className="detail-item"><label>Type</label><p>{image.imageType}</p></div>
          <div className="detail-item"><label>OS</label><p><OsIcon name={image.osType || image.blueprintName} size={18} fallback="" /> {image.osType || '-'} {image.osVersion || ''}</p></div>
          <div className="detail-item"><label>Architecture</label><p>{formatArch(image.architecture, true)}</p></div>
          <div className="detail-item"><label>Status</label><p>{image.status}</p></div>
          <div className="detail-item"><label>Workflow</label>
            <p>{image.workflowName ? <Link to={`/build/${image.workflowName}`} className="link">{image.workflowName}</Link> : '-'}</p>
          </div>
          <div className="detail-item"><label>Created</label><p>{image.createdAt ? new Date(image.createdAt).toLocaleString() : '-'}</p></div>
          {image.description && <div className="detail-item" style={{ gridColumn: '1 / -1' }}><label>Description</label><p>{image.description}</p></div>}
        </div>
      </div>

      {image.platforms?.length > 0 && (
        <div className="card">
          <h3>Image Platforms</h3>
          <table>
            <thead>
              <tr>
                <th>Organization</th>
                <th>Cloud</th>
                <th>Region</th>
                <th>Image ID</th>
                <th>Critical</th>
                <th>High</th>
                <th>Medium</th>
                <th>Compliance</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {image.platforms.map((p) => (
                <tr key={p.id} className={selectedPlatformId === p.id ? 'row-selected' : ''}>
                  <td>{p.organization || '-'}</td>
                  <td>{p.cloudProvider || p.platformName}</td>
                  <td>{p.region}</td>
                  <td><code>{p.imageId}</code></td>
                  <td style={{ color: p.criticalCount > 0 ? '#d63031' : undefined, fontWeight: p.criticalCount > 0 ? 'bold' : undefined }}>
                    {p.criticalCount}
                  </td>
                  <td style={{ color: p.highCount > 0 ? '#e17055' : undefined }}>
                    {p.highCount}
                  </td>
                  <td>{p.mediumCount}</td>
                  <td><ComplianceBadge compliant={p.compliant} critical={p.criticalCount} high={p.highCount} /></td>
                  <td>
                    <button className="btn btn-primary" style={{ padding: '4px 12px', fontSize: 12 }}
                      onClick={() => setSelectedPlatformId(selectedPlatformId === p.id ? null : p.id)}>
                      {selectedPlatformId === p.id ? 'Hide CVEs' : 'View CVEs'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedPlatformId && (
        <div className="card">
          <h3>CVE Findings</h3>
          <CveTable platformId={selectedPlatformId} />
        </div>
      )}

      <div className="actions" style={{ flexWrap: 'wrap' }}>
        <Link to={`/scorecard/${id}`} className="btn btn-primary" style={{ background: '#00b894' }}>
          Health Scorecard
        </Link>
        <Link to={`/sbom/${id}`} className="btn btn-primary" style={{ background: '#00cec9' }}>
          View SBOM
        </Link>
        <Link to={`/lineage/${id}`} className="btn btn-primary" style={{ background: '#6c5ce7' }}>
          View Lineage
        </Link>
        <Link to={`/tests/${id}`} className="btn btn-primary" style={{ background: '#e17055' }}>
          Test Results
        </Link>
        <Link to={`/compliance/${id}`} className="btn btn-primary" style={{ background: '#636e72' }}>
          Compliance Report
        </Link>
        <Link to={`/fleet/instances?amiIds=${id}`} className="btn btn-primary" style={{ background: '#0984e3' }}>
          Deployed Instances
        </Link>
        <Link to={`/compare?imageA=${id}`} className="btn btn-primary" style={{ background: '#74b9ff' }}>
          Compare
        </Link>
        <button className="btn btn-danger" onClick={handleDelete} disabled={deleting}>
          {deleting ? 'Deleting...' : 'Delete Image'}
        </button>
      </div>
    </div>
  );
}
